import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { User, Team, TeamSettings } from '@/types/User'
import { authClient } from '@/lib/auth-client'
import { postAuthEvent, type OtherTabTeam } from '@/lib/auth-channel'
import { forgetLastTeam, migrateLegacySharedAuth } from '@/lib/tab-team'
import { clearPrefsOnSignOut, prefStateStorage, readPref, writePref } from '@/lib/ui-prefs'
import { AUTH_IN_PROGRESS, clearAuthMarkers } from '@/lib/auth-prefs'
import { setTeamSettings, clearTeamSettingsCache } from '@/utils/teamSettingsCache'
import { clearLoaderCache } from '@/hooks/loader-store'
// team-exit imports this store too; each only uses the other inside functions, so the cycle is safe.
import { beginTeamExit, cancelTeamExit } from '@/utils/team-exit'

const isDev = () => process.env.NODE_ENV === 'development'

// Before the store reads its own copy: move the old shared session's team to the new-tab default.
migrateLegacySharedAuth()

export interface AuthResponse {
  token?: string;
  pending_invite_token?: string | null;
  user?: {
    id: string;
    email: string;
    displayname?: string;
    photourl?: string;
    role: string;
    team_displayname?: string;
    teams?: {
      id: string;
      displayname: string;
      shopify_store_domain: string;
      status?: string;
    }[];
    active_team?: {
      id: string;
      displayname: string;
      shopify_store_domain: string;
      status?: string;
      settings?: any;
    };
    team_settings?: any;
  };
}

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  authAttempted: boolean;
  /** Another tab of this person switched to this team; this tab offers to follow (not persisted). */
  otherTabTeam: OtherTabTeam | null;
  /** /auth/session failed with no answer or a server error at page load: still signed in (not persisted). */
  sessionUnreachable: boolean;
  /** Renewing the session mid-session failed the same way: a banner offers Retry (not persisted). */
  sessionRefreshFailed: boolean;
  /**
   * The tab is leaving its team (a switch, a sign-out elsewhere): the team pages are swapped for
   * this label so they unmount before the next session is stored. See utils/team-exit.ts (not persisted).
   */
  teamExit: { label: string } | null;
  login: () => Promise<void>;
  /**
   * Signs out, clears this browser's session data, then loads `loginPath` (default `/login`) as a
   * full page, so no store keeps the last session's data in memory. If the server didn't end the
   * session, the tab is only signed out here and goes to /login without a reload. Ask about unsaved
   * work first.
   */
  logout: (loginPath?: string) => Promise<void>;
  setUser: (user: User | null) => void;
  setError: (error: string | null) => void;
  setLoading: (loading: boolean) => void;
  setAuthAttempted: (attempted: boolean) => void;
  setOtherTabTeam: (team: OtherTabTeam | null) => void;
  setSessionUnreachable: (unreachable: boolean) => void;
  setSessionRefreshFailed: (failed: boolean) => void;
  setTeamExit: (teamExit: { label: string } | null) => void;
  mergeActiveTeamSettings: (teamId: string, settings: TeamSettings) => void;
  updateUserWithTeam: (team: Team, token?: string, role?: string) => void;
  updateUserAndToken: (userData: AuthResponse) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      loading: true,
      error: null,
      authAttempted: false,
      otherTabTeam: null,
      sessionUnreachable: false,
      sessionRefreshFailed: false,
      teamExit: null,
      
      setUser: (user) => set({ user }),
      setError: (error) => set({ error }),
      setLoading: (loading) => set({ loading }),
      setAuthAttempted: (authAttempted) => set({ authAttempted }),
      setOtherTabTeam: (otherTabTeam) => set({ otherTabTeam }),
      setSessionUnreachable: (sessionUnreachable) => set({ sessionUnreachable }),
      setSessionRefreshFailed: (sessionRefreshFailed) => set({ sessionRefreshFailed }),
      setTeamExit: (teamExit) => set({ teamExit }),
      mergeActiveTeamSettings: (teamId, settings) => {
        const { user } = get();
        if (!user?.active_team || user.active_team.id !== teamId) return;

        const mergedSettings = {
          ...user.active_team.settings,
          ...settings,
        };

        setTeamSettings(teamId, mergedSettings);
        set({
          user: {
            ...user,
            active_team: {
              ...user.active_team,
              settings: mergedSettings,
            },
          },
        });
      },
      
      updateUserAndToken: (userData) => {
        if (!userData.user) {
          // Not the object itself: it carries the token.
          console.error('Invalid user data received');
          set({ loading: false, error: 'Invalid user data received' });
          return;
        }

        set({
          user: {
            id: userData.user.id,
            email: userData.user.email,
            displayName: userData.user.displayname || '',
            photoURL: userData.user.photourl || '',
            role: userData.user.role || 'user',
            token: userData.token || '',
            teams: userData.user.teams || [],
            active_team: userData.user.active_team,
            team_displayname: userData.user.team_displayname || (userData.user.active_team?.displayname || ''),
            pendingInviteToken: userData.pending_invite_token ?? null,
          },
          loading: false,
          error: null,
          sessionUnreachable: false,
          sessionRefreshFailed: false,
        });
        
        if (userData.token) {
          if (userData.user.active_team?.id) {
            const settings = userData.user.active_team.settings || userData.user.team_settings;
            
            if (settings) {
              if (isDev()) console.log('Setting team settings from user data:', userData.user.active_team.id, settings);
              setTeamSettings(userData.user.active_team.id, settings);
            } else if (isDev()) {
              console.log('No settings found in user data');
            }
          }
        }
      },
      
      updateUserWithTeam: (team, token, role) => {
        const { user } = get();
        if (user) {
          if (isDev()) console.log('Updating user with team:', team, token ? 'with new token' : 'without new token');
          
          // Use settings from team object if available
          if (team.settings) {
            if (isDev()) console.log('Setting team settings from team object:', team.id, team.settings);
            setTeamSettings(team.id, team.settings);
          }

          const existingTeams = user.teams || [];
          const teams = existingTeams.some((t) => t.id === team.id)
            ? existingTeams
            : [...existingTeams, team];
          
          set({
            user: {
              ...user,
              teams,
              active_team: team,
              team_displayname: team.displayname,
              token: token || user.token,
              role: role || user.role,
              pendingInviteToken: null,
            }
          });
        }
      },
      
      login: async () => {
        set({ loading: true, error: null, authAttempted: true });
        
        try {
          writePref(AUTH_IN_PROGRESS, true);
          
          await authClient.signIn.social({
            provider: 'google',
            callbackURL: `${window.location.origin}/login`,
            // A refused sign-in (no invite, T-74) comes back to /login?error=…, not BetterAuth's own page.
            errorCallbackURL: `${window.location.origin}/login`,
          });
          
          if (readPref(AUTH_IN_PROGRESS)) {
            setTimeout(() => {
              if (readPref(AUTH_IN_PROGRESS)) {
                if (isDev()) console.log('Auth appears stuck, forcing page reload');
                clearAuthMarkers();
                window.location.reload();
              }
            }, 3000);
          }
          
          // User is set after OAuth return via AuthProvider session exchange
        } catch (err) {
          const errorMessage = (err as Error).message || 'Failed to login';
          set({ error: errorMessage, loading: false });
          console.error(err);
          clearAuthMarkers();
        }
      },
      
      logout: async (loginPath = '/login') => {
        const userId = get().user?.id;
        // Unmount the team pages first, so their last clean-ups run before the clear below and their
        // guards don't ask again on the way out (the caller has asked).
        await beginTeamExit('Signing out…');
        let signedOut = false;
        try {
          // Bounded, so a server that never answers can't leave the tab on "Signing out…".
          const res = await authClient.signOut({ fetchOptions: { signal: AbortSignal.timeout(10_000) } });
          signedOut = !res?.error;
          if (res?.error) console.error('Sign out request failed:', res.error);
        } catch (err) {
          console.error('Sign out request failed:', err);
        } finally {
          // Always clear local session — remote sign-out can fail when the session is already dead
          set({
            user: null,
            loading: false,
            error: null,
            otherTabTeam: null,
            sessionUnreachable: false,
            sessionRefreshFailed: false,
          });
          forgetLastTeam();
          clearTeamSettingsCache();
          clearLoaderCache();
          // Drafts, filters, the label queue and other team data go; layout prefs stay (decision 31 a).
          clearPrefsOnSignOut();
          // The browser has one sign-in, so every other tab signs out too (decision 7 a).
          if (userId) postAuthEvent({ type: 'signed-out', userId });
          // A full page load, not a client-side route change: zustand stores (products, labels,
          // inbox…) would otherwise keep the last session's data until the next reload. Only once
          // the server has ended the session: a fresh page would otherwise sign straight back in
          // with the same cookie.
          if (signedOut) window.location.assign(loginPath);
          // Otherwise the tab is only signed out here, without a reload: undo the exit, so a sign-in
          // in this tab has its guards and team pages back. Callers outside the route guard go to
          // /login themselves.
          else cancelTeamExit();
        }
      }
    }),
    {
      name: 'auth-storage',
      // Per tab, so each tab keeps its own team and token (T-155). A new tab starts empty and asks
      // the server for the last team (lib/tab-team.ts).
      storage: createJSONStorage(() => prefStateStorage('session')),
      partialize: (state) => ({ user: state.user }),
    }
  )
); 